import { useEffect, useState, type ReactNode } from "react";
import { getPropertySummary } from "../api/client";
import type { PropertySummary, SoundscoreFact, WalkScoreFact } from "../types/api";

/**
 * Free-tier property summary card (Task 9.9a). Renders above the chat with
 * key facts sourced from web search + public records (NOT ATTOM — that's
 * Pro). Progressive: shows a skeleton instantly, then fills when the
 * backend's web-search + LLM extraction returns (~2-4s). Missing fields
 * render as "—" rather than disappearing, so the card shape is stable.
 */

/**
 * Pro extras (Task 9.7a → property-facts Task 5): licensed ATTOM facts from the
 * expandedprofile + AVM calls. On Pro these are the card's ONLY source — the
 * free-tier web search is not fired (John, 2026-06-09: "the web search on pro
 * is not acceptable"; the licensed listing data rides the call we already
 * make). The Active Listing window is the hero "here's the intelligence you
 * paid for" surface, so it carries both the listing-parity facts and the
 * WOW licensed facts. `originalLoan` is the ORIGINATION amount (R4 — labeled
 * "Original loan", never "current balance"). Every leaf is nullable.
 */
export interface ProExtras {
  // Listing-parity facts (licensed — replace the free web-search specs on Pro)
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  /** Asking price ÷ living sqft — the listing's $/sqft. */
  pricePerSqft: number | null;
  yearBuilt: number | null;
  lotSizeAcres: number | null;
  lastSalePrice: number | null;
  lastSaleDate: string | null;
  // WOW licensed facts — the upgrade drivers buyers can't pull from portals
  avmValue: number | null;
  avmChangePct: number | null;
  /** ATTOM AVM confidence score (`scr`, 0-100) → shown as a certainty %. */
  avmConfidence: number | null;
  floodZone: string | null;
  /** Owner equity %. */
  equityPct: number | null;
  /** Owner-occupied (true) vs investor/absentee (false) from `absenteeInd`. */
  ownerOccupied: boolean | null;
  annualTax: number | null;
  assessedValue: number | null;
  marketValue: number | null;
  permitCount: number | null;
  /** FEMA NRI {hazard: rating} across all 18 hazards; shown only when above-low. */
  nriHazards: Record<string, string> | null;
  // Neighborhood (area) facts — crime/air indices are 100 = U.S. average.
  crimeIndex: number | null;
  airQualityIndex: number | null;
  ownerOccupancyPct: number | null;
  vacancyPct: number | null;
  medianCommuteMin: number | null;
  medianHouseholdIncome: number | null;
  bachelorsPlusPct: number | null;
  /** Walk Score (licensed) — carries the required logo + backlink attribution. */
  walkScore: WalkScoreFact | null;
  /** HowLoud Soundscore (licensed) — higher = quieter. */
  soundscore: SoundscoreFact | null;
}

// NRI ratings above "Relatively Low" are worth surfacing; below that the
// hazard is irrelevant for the area and would just add noise (no earthquake
// row in Savannah, but it shows in the Bay Area).
const HAZARD_RANK: Record<string, number> = {
  "very low": 0,
  "relatively low": 1,
  "relatively moderate": 2,
  "relatively high": 3,
  "very high": 4,
};

function hazardRank(rating: string | null): number {
  return rating != null ? (HAZARD_RANK[rating.trim().toLowerCase()] ?? -1) : -1;
}

function isElevatedHazard(rating: string | null): boolean {
  return hazardRank(rating) >= 2;
}

interface Props {
  placeId: string;
  address: string;
  /** Navigate to upgrade — used by the contextual thin-data nudge (9.9b). */
  onUpgradeClick?: () => void;
  /** When present, render the Pro-extended card (licensed facts, no web search). */
  pro?: ProExtras;
  /** Pro only: true while the score (and thus the licensed facts) is computing. */
  proLoading?: boolean;
}

const DASH = "—";

function money(n: number | null | undefined): string {
  return n != null ? `$${n.toLocaleString()}` : DASH;
}

function num(n: number | null | undefined): string {
  return n != null ? `${n.toLocaleString()}` : DASH;
}

// Years are plain integers — never thousands-separated (1981, not "1,981").
function year(n: number | null | undefined): string {
  return n != null ? `${Math.trunc(n)}` : DASH;
}

// ISO datetime → US date ("2021-03-12T00:00:00+00:00" → "03/12/2021"). Slices
// the date part directly so there's no timezone shift from `new Date`.
function saleDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${m}/${d}/${y}` : "";
}

// Lots under half an acre read more naturally in square feet (10,237 sq ft)
// than as a fraction of an acre (0.24 ac); 1 acre = 43,560 sq ft.
function lotLabel(acres: number | null | undefined): string {
  if (acres == null) return DASH;
  if (acres < 0.5) return `${Math.round(acres * 43560).toLocaleString()} sq ft`;
  return `${acres.toFixed(2)} ac`;
}

function statusLabel(s: PropertySummary["listing_status"]): string {
  switch (s) {
    case "active":
      return "Active listing";
    case "off-market":
      return "Off-market";
    case "pending":
      return "Sale pending";
    default:
      return "Unknown";
  }
}

function roomClass(label: string | undefined): string {
  const l = (label || "").toLowerCase();
  if (l.startsWith("strong")) return "room-strong";
  if (l.startsWith("moderate")) return "room-moderate";
  if (l.startsWith("limited")) return "room-limited";
  return "room-unknown";
}

// Rotating progress lines while the summary loads — the search + LLM take a
// few seconds, so cycling these reads as steady progress rather than a stall.
const LOADING_STEPS = [
  "Searching listings & price history…",
  "Pulling county assessor records…",
  "Checking flood & hazard data…",
  "Writing your buyer summary…",
];

/** One Pro fact row; shows a shimmer while the licensed record is still loading. */
function ProItem({ label, loading, children }: { label: ReactNode; loading: boolean; children: ReactNode }) {
  return (
    <div className="summary-item summary-item-pro">
      <dt>{label}</dt>
      <dd>{loading ? <span className="pt-skel pt-skel-line" aria-hidden /> : children}</dd>
    </div>
  );
}

/**
 * Collapsed "Natural hazards" cell — folds Flood zone + the elevated NRI
 * hazards into one grid space (a barrier island can flag 5+). Shows a flagged
 * count; expands to the full list. Flood zone always appears in the detail;
 * only above-low NRI hazards do.
 */
function NaturalHazardsItem({
  loading,
  floodZone,
  elevatedHazards,
  haveHazardData,
}: {
  loading: boolean;
  floodZone: string | null;
  elevatedHazards: Array<[string, string]>;
  haveHazardData: boolean;
}) {
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <div className="summary-item summary-item-pro">
        <dt>Natural hazards</dt>
        <dd><span className="pt-skel pt-skel-line" aria-hidden /></dd>
      </div>
    );
  }

  // Flood zone X (and X-shaded) = minimal; anything else is a flagged zone.
  const floodRisky = !!floodZone && !floodZone.toUpperCase().startsWith("X");

  const detail: Array<[string, string]> = [["Flood zone", floodZone || DASH]];
  for (const [name, rating] of elevatedHazards) detail.push([`${name} risk`, rating]);
  if (haveHazardData && elevatedHazards.length === 0) {
    detail.push(["Other hazards", "Low across the board"]);
  }

  // Headline = the flagged hazards, each on its OWN line (flood first if risky,
  // then the worst-first NRI hazards). The ▸ expands the full detail.
  const flaggedItems: string[] = [];
  if (floodRisky) flaggedItems.push(`Flood zone ${floodZone}`);
  for (const [name, rating] of elevatedHazards) flaggedItems.push(`${name}: ${rating}`);

  return (
    <div className="summary-item summary-item-pro">
      <dt>Natural hazards</dt>
      <dd>
        <button
          type="button"
          className="hazard-toggle"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span className={`hazard-caret${open ? " is-open" : ""}`} aria-hidden>▸</span>
          {flaggedItems.length
            ? flaggedItems[0]
            : haveHazardData
              ? "Low across the board"
              : DASH}
        </button>
        {open && (
          <ul className="hazard-detail">
            {detail.map(([label, val]) => (
              <li key={label}>
                <span className="hazard-detail-label">{label}</span>
                <span className="hazard-detail-val">{val}</span>
              </li>
            ))}
          </ul>
        )}
      </dd>
    </div>
  );
}

/**
 * A neighborhood metric row: a headline ("Crime  113  ▸") that expands on click
 * to reveal its bar/detail. Each row owns its open state, so Crime, Pollution,
 * and Noise expand independently.
 */
function MetricRow({ label, value, children }: { label: string; value?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="metric-row">
      <button
        type="button"
        className="metric-row-head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={`hazard-caret${open ? " is-open" : ""}`} aria-hidden>▸</span>
        <span className="metric-row-label">{label}</span>
        {value ? <span className="metric-row-val">{value}</span> : null}
      </button>
      {open && <div className="metric-row-detail">{children}</div>}
    </li>
  );
}

/**
 * Crime / Pollution diverging bar where 100 = U.S. average: the centerline is
 * the U.S. average, the fill shows how far this area sits from it. Deliberately
 * MONOCHROME (no red/green good-bad) — color-coding crime would be fair-housing
 * steering. Lower = less of the thing.
 */
function DivergingBar({ value }: { value: number }) {
  const dir = value < 100 ? "below U.S. avg" : value > 100 ? "above U.S. avg" : "at U.S. avg";
  // Clamp to 0–200 so 100 sits at the bar's center (50%); the dot marks where
  // this area falls relative to the U.S. average.
  const pos = Math.max(0, Math.min(200, value)) / 2; // 0–100 (%)
  return (
    <>
      <div className="index-bar" aria-hidden>
        <span className="index-bar-tick" />
        <span className="index-bar-marker" style={{ left: `${pos}%` }} />
      </div>
      <div className="metric-caption">
        {dir}
        <br />
        100 = U.S. average
      </div>
    </>
  );
}

/**
 * Noise (HowLoud Soundscore) continuum: 50 (loud) → 100 (quiet), shown as a
 * marker on a Louder→Quieter track (no U.S.-average center — it's an absolute
 * scale, not an index). Monochrome, consistent with the diverging bars.
 */
function NoiseBar({ score }: { score: number }) {
  // Same full-width bar as Crime/Pollution: 100 (quiet) far left → 50 (loud)
  // far right, with 75 at the centerline. Left of center = quieter, right of
  // center = louder. 100 → 0%, 50 → 100%.
  const pos = Math.max(0, Math.min(100, (100 - score) * 2));
  return (
    <div className="index-bar" aria-hidden>
      <span className="index-bar-tick" />
      <span className="index-bar-marker" style={{ left: `${pos}%` }} />
    </div>
  );
}

/**
 * Collapsed "Neighborhood" cell — area facts from the ATTOM community call we
 * already pay for, folded into one grid space. Crime + pollution indices show a
 * diverging bar vs the U.S. average; the rest are plain figures (the agent
 * prompt forbids steering on the income/education ones). Renders nothing when
 * no neighborhood data came back.
 */
function NeighborhoodItem({
  loading,
  crimeIndex,
  airQualityIndex,
  ownerOccupancyPct,
  vacancyPct,
  medianCommuteMin,
  medianHouseholdIncome,
  bachelorsPlusPct,
  soundscore,
}: {
  loading: boolean;
  crimeIndex: number | null;
  airQualityIndex: number | null;
  ownerOccupancyPct: number | null;
  vacancyPct: number | null;
  medianCommuteMin: number | null;
  medianHouseholdIncome: number | null;
  bachelorsPlusPct: number | null;
  soundscore: SoundscoreFact | null;
}) {
  if (loading) {
    return (
      <div className="summary-item summary-item-pro">
        <dt>Neighborhood</dt>
        <dd><span className="pt-skel pt-skel-line" aria-hidden /></dd>
      </div>
    );
  }

  // Demographics — always visible below the indices (owner-occupancy, vacancy,
  // commute, income, education). Plain facts; the agent prompt forbids steering
  // on the income/education ones.
  const demo: string[] = [];
  if (ownerOccupancyPct != null) demo.push(`Owner-occ ${Math.round(ownerOccupancyPct)}%`);
  if (vacancyPct != null) demo.push(`Vacancy ${Math.round(vacancyPct)}%`);
  if (medianCommuteMin != null) demo.push(`Commute ${Math.round(medianCommuteMin)} min`);
  if (medianHouseholdIncome != null) demo.push(`Median income $${Math.round(medianHouseholdIncome).toLocaleString()}`);
  if (bachelorsPlusPct != null) demo.push(`Bachelor's+ ${Math.round(bachelorsPlusPct)}%`);

  const hasIndices = crimeIndex != null || airQualityIndex != null || soundscore?.score != null;
  if (!hasIndices && demo.length === 0) return null;

  // Noise sub-sources as WORDS — the raw integers are undocumented/misleading;
  // the Calm/Active/Busy labels are the meaningful, direction-consistent signal.
  const noiseSources: string[] = [];
  if (soundscore?.traffictext) noiseSources.push(`Traffic: ${soundscore.traffictext}`);
  if (soundscore?.airportstext) noiseSources.push(`Airports: ${soundscore.airportstext}`);
  if (soundscore?.localtext) noiseSources.push(`Local: ${soundscore.localtext}`);

  return (
    <div className="summary-item summary-item-pro">
      <dt>Neighborhood</dt>
      <dd>
        <ul className="metric-list">
          {crimeIndex != null && (
            <MetricRow label="Crime" value={`${Math.round(crimeIndex)}`}>
              <DivergingBar value={crimeIndex} />
            </MetricRow>
          )}
          {airQualityIndex != null && (
            <MetricRow label="Pollution" value={`${Math.round(airQualityIndex)}`}>
              <DivergingBar value={airQualityIndex} />
            </MetricRow>
          )}
          {soundscore?.score != null && (
            <MetricRow label="Noise" value={`${Math.round(soundscore.score)}`}>
              <NoiseBar score={soundscore.score} />
              <div className="metric-caption">
                100 = quiet
                <br />
                50 = loud
              </div>
              {noiseSources.length > 0 && (
                <div className="noise-sources">
                  {noiseSources.map((s) => (
                    <span key={s} className="metric-line">{s}</span>
                  ))}
                </div>
              )}
            </MetricRow>
          )}
          {demo.length > 0 && (
            <MetricRow label="Other">
              {demo.map((d) => (
                <span key={d} className="metric-line">{d}</span>
              ))}
            </MetricRow>
          )}
        </ul>
      </dd>
    </div>
  );
}

/**
 * Walk Score cell (licensed). Renders the Walk Score® logo + score, linked back
 * to Walk Score's address page — this attribution is REQUIRED by their API
 * license (do not strip the logo or the link). Transit/Bike scores are appended
 * when the area has coverage (many areas, e.g. downtown Savannah, return walk +
 * bike but no transit). Renders nothing when no walk score came back.
 */
function WalkScoreItem({ loading, walkScore }: { loading: boolean; walkScore: WalkScoreFact | null }) {
  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <div className="summary-item summary-item-pro">
        <dt>Walk Score</dt>
        <dd><span className="pt-skel pt-skel-line" aria-hidden /></dd>
      </div>
    );
  }
  if (!walkScore || walkScore.walkscore == null) return null;

  // Expanded detail: description + transit/bike (coverage-dependent).
  const details: string[] = [];
  if (walkScore.description) details.push(walkScore.description);
  if (walkScore.transit?.score != null) details.push(`Transit ${walkScore.transit.score}`);
  if (walkScore.bike?.score != null) details.push(`Bike ${walkScore.bike.score}`);

  // Logo + score, linked to Walk Score's address page — REQUIRED attribution
  // (do not strip the logo or the link).
  const badge = (
    <>
      {walkScore.logo_url && <img className="walkscore-logo" src={walkScore.logo_url} alt="Walk Score" />}
      <span className="walkscore-num">{walkScore.walkscore}</span>
    </>
  );

  return (
    <div className="summary-item summary-item-pro">
      <dt>Walk Score</dt>
      <dd>
        <div className="walkscore-head">
          {details.length > 0 && (
            <button
              type="button"
              className="walkscore-toggle"
              aria-expanded={open}
              aria-label="Walk Score details"
              onClick={() => setOpen((o) => !o)}
            >
              <span className={`hazard-caret${open ? " is-open" : ""}`} aria-hidden>▸</span>
            </button>
          )}
          {walkScore.link ? (
            <a className="walkscore-link" href={walkScore.link} target="_blank" rel="noopener noreferrer">
              {badge}
            </a>
          ) : (
            <span className="walkscore-link">{badge}</span>
          )}
        </div>
        {open && details.length > 0 && (
          <div className="metric-row-detail">
            {details.map((d) => (
              <span key={d} className="metric-line">{d}</span>
            ))}
          </div>
        )}
      </dd>
    </div>
  );
}

export function PropertySummaryCard({ placeId, address, onUpgradeClick, pro, proLoading }: Props) {
  const isPro = !!pro;
  const [summary, setSummary] = useState<PropertySummary | null>(null);
  const [loading, setLoading] = useState(!isPro);
  const [failed, setFailed] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  useEffect(() => {
    // Pro renders entirely from licensed props — do NOT fire the free-tier
    // web search (it's the wrong source for paid users and a needless call).
    if (isPro) return;
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setSummary(null);
    getPropertySummary(placeId, address)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [placeId, address, isPro]);

  // Advance the progress line every ~2.5s while loading; stop the moment the
  // data lands (clamped so it never runs off the end). Free tier only.
  useEffect(() => {
    if (isPro || !loading) return;
    setStepIdx(0);
    const id = window.setInterval(() => {
      setStepIdx((i) => Math.min(i + 1, LOADING_STEPS.length - 1));
    }, 2500);
    return () => window.clearInterval(id);
  }, [loading, placeId, address, isPro]);

  const specs = summary?.specs;
  const room = summary?.negotiation_room;
  const pl = !!proLoading;

  // ---- Pro: the hero facts window, sourced entirely from licensed data ----
  if (pro) {
    const permits =
      pro.permitCount == null
        ? DASH
        : pro.permitCount === 0
          ? "None on record"
          : `${pro.permitCount} recent`;
    const occupancy =
      pro.ownerOccupied == null ? DASH : pro.ownerOccupied ? "Owner-occupied" : "Investor / absentee";
    const taxLine = pro.annualTax != null ? `${money(pro.annualTax)}/yr` : DASH;
    const skel = <span className="pt-skel pt-skel-line" aria-hidden />;

    // FEMA NRI hazards: surface only the ones rated above "Low" for this area
    // (Savannah → wildfire/heat; a barrier island → coastal flooding/strong
    // wind; the Bay Area → earthquake), worst first. When data is present but
    // nothing is elevated, say so; when the lookup gave us nothing, stay silent
    // rather than falsely claim "all low."
    const haveHazardData = pro.nriHazards != null && Object.keys(pro.nriHazards).length > 0;
    const elevatedHazards = Object.entries(pro.nriHazards ?? {})
      .filter(([, r]) => isElevatedHazard(r))
      .sort((a, b) => hazardRank(b[1]) - hazardRank(a[1]));

    return (
      <section
        className="property-summary-card"
        aria-label="Property facts"
        aria-busy={pl}
      >
        {/* Rows 1-2: listing details (MLS-style facts). */}
        <dl className="summary-grid">
          <div className="summary-item">
            <dt>Last sale</dt>
            <dd>
              {pl ? skel : (
                <>
                  {money(pro.lastSalePrice)}
                  {pro.lastSaleDate ? (
                    <>
                      <br />
                      {saleDate(pro.lastSaleDate)}
                    </>
                  ) : (
                    ""
                  )}
                </>
              )}
            </dd>
          </div>
          <div className="summary-item">
            <dt>Beds / Baths</dt>
            <dd>{pl ? skel : `${num(pro.beds)} / ${num(pro.baths)}`}</dd>
          </div>
          <div className="summary-item">
            <dt>Sq ft</dt>
            <dd>{pl ? skel : num(pro.sqft)}</dd>
          </div>
          <div className="summary-item">
            <dt>Price / sq ft</dt>
            <dd>{pl ? skel : pro.pricePerSqft != null ? `$${Math.round(pro.pricePerSqft).toLocaleString()}` : DASH}</dd>
          </div>
          <div className="summary-item">
            <dt>Year built</dt>
            <dd>{pl ? skel : year(pro.yearBuilt)}</dd>
          </div>
          <div className="summary-item">
            <dt>Lot size</dt>
            <dd>{pl ? skel : lotLabel(pro.lotSizeAcres)}</dd>
          </div>
        </dl>

        {/* Row 3+: licensed PriceTilt intelligence (the WOW facts). */}
        <dl className="summary-grid summary-grid-licensed">
          <ProItem
            label={
              <>
                Automated Valuation Model
                <br />
                (AVM)
              </>
            }
            loading={pl}
          >
            {money(pro.avmValue)}
            {pro.avmChangePct != null ? (
              <>
                <br />• {pro.avmChangePct >= 0 ? "+" : ""}
                {pro.avmChangePct.toFixed(1)}% 12mo
              </>
            ) : null}
            {pro.avmConfidence != null ? (
              <>
                <br />• {Math.round(pro.avmConfidence)}% confidence
              </>
            ) : null}
          </ProItem>
          {(pl || pro.assessedValue != null) && (
            <ProItem label="Assessed value" loading={pl}>
              {money(pro.assessedValue)}
            </ProItem>
          )}
          {(pl || pro.marketValue != null) && (
            <ProItem label="Market value" loading={pl}>
              {money(pro.marketValue)}
            </ProItem>
          )}
          <ProItem label="Property tax" loading={pl}>
            {taxLine}
          </ProItem>
          <ProItem label="Owner equity" loading={pl}>
            {pro.equityPct != null ? `≈${Math.round(pro.equityPct)}%` : DASH}
          </ProItem>
          <ProItem label="Occupancy" loading={pl}>
            {occupancy}
          </ProItem>
          <ProItem label="Permits" loading={pl}>
            {permits}
          </ProItem>
          {/* Flood zone + elevated NRI hazards, folded into one expandable cell. */}
          <NaturalHazardsItem
            loading={pl}
            floodZone={pro.floodZone}
            elevatedHazards={elevatedHazards}
            haveHazardData={haveHazardData}
          />
          {/* Walk Score (licensed) — logo + score + backlink (attribution required). */}
          <WalkScoreItem loading={pl} walkScore={pro.walkScore} />
          {/* Area facts (crime, air quality, demographics, noise) — community call + HowLoud. */}
          <NeighborhoodItem
            loading={pl}
            crimeIndex={pro.crimeIndex}
            airQualityIndex={pro.airQualityIndex}
            ownerOccupancyPct={pro.ownerOccupancyPct}
            vacancyPct={pro.vacancyPct}
            medianCommuteMin={pro.medianCommuteMin}
            medianHouseholdIncome={pro.medianHouseholdIncome}
            bachelorsPlusPct={pro.bachelorsPlusPct}
            soundscore={pro.soundscore}
          />
        </dl>

        <p className="summary-foot">
          {pl ? "Pulling your licensed property record…" : "Licensed PriceTilt Pro property data."}
        </p>
      </section>
    );
  }

  // ---- Free tier: web-search summary + upgrade CTA (unchanged) ----
  return (
    <section
      className={`property-summary-card${loading ? " is-loading" : ""}`}
      aria-label="Property summary"
      aria-busy={loading}
    >
      <div className="summary-row summary-head">
        <span className="summary-status">
          {loading ? "" : statusLabel(summary?.listing_status ?? null)}
        </span>
        {room && (
          <span className={`summary-room ${roomClass(room.label)}`} title={room.rationale}>
            {room.label} to negotiate
          </span>
        )}
      </div>

      {summary?.narrative && <p className="summary-narrative">{summary.narrative}</p>}

      <dl className="summary-grid">
        <div className="summary-item">
          <dt>Last sale</dt>
          <dd>
            {money(summary?.last_sale?.price)}
            {summary?.last_sale?.date ? ` (${summary.last_sale.date})` : ""}
          </dd>
        </div>
        <div className="summary-item">
          <dt>Beds / Baths</dt>
          <dd>
            {num(specs?.beds)} / {num(specs?.baths)}
          </dd>
        </div>
        <div className="summary-item">
          <dt>Sq ft</dt>
          <dd>{num(specs?.sqft)}</dd>
        </div>
        <div className="summary-item">
          <dt>Year built</dt>
          <dd>{num(specs?.year_built)}</dd>
        </div>
        <div className="summary-item">
          <dt>Lot size</dt>
          <dd>{specs?.lot_size || DASH}</dd>
        </div>
        <div className="summary-item">
          <dt>Est. value{summary?.value_range?.source ? ` (${summary.value_range.source})` : ""}</dt>
          <dd>
            {summary?.value_range && (summary.value_range.low != null || summary.value_range.high != null)
              ? `${money(summary.value_range.low)} – ${money(summary.value_range.high)}`
              : DASH}
          </dd>
        </div>
      </dl>

      <p className="summary-foot">
        {loading ? (
          LOADING_STEPS[stepIdx]
        ) : failed ? (
          "Couldn't load property details right now — ask the assistant below."
        ) : (
          <>
            Summary from public records. To see the owner&rsquo;s equity — who
            you&rsquo;re really negotiating with — plus verified fair value and
            your Tilt Map offer range,{" "}
            <button type="button" className="link-button" onClick={onUpgradeClick}>
              Upgrade to PriceTilt Pro
            </button>
            .
          </>
        )}
      </p>

      {!loading && onUpgradeClick && summary?.data_confidence === "low" && (
        <div className="summary-upgrade-nudge" role="note">
          <p>
            <strong>The public web is thin on this property.</strong> PriceTilt
            Pro pulls its verified record, AVM (automated value estimate), and
            sale history instantly — even when listings and public sources come
            up empty.
          </p>
          <button type="button" className="upgrade-cta" onClick={onUpgradeClick}>
            See it with PriceTilt Pro
          </button>
        </div>
      )}
    </section>
  );
}
